/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logPivotClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { modelGuidancePivotKeyToName } from 'src/core/utils/telemetryUtils';
import { ModelPerformanceModalDto } from 'src/customText/appShell/modelDetailsPages/components/TestDocumentDetailsModal';
import { CustomTextTestSetDetailsTab } from 'src/customText/appShell/modelDetailsPages/tabs/CustomTextTestSetDetailsTab';
import { ConfusionMatrixTab } from 'src/modules/modelGuidance/components/tabs/ConfusionMatrixTab';
import { DataDistributionTab } from 'src/modules/modelGuidance/components/tabs/DataDistributionTab';
import { ModelEvaluationTab } from 'src/modules/modelGuidance/components/tabs/ModelEvaluationTab';
import { OverviewTab } from 'src/modules/modelGuidance/components/tabs/OverviewTab';

const messages = defineMessages({
    overview: {
        id: 'customText.customTextModelDetailsPage.overview',
        defaultMessage: 'Overview'
    },
    classificationTitle: {
        id: 'customText.customTextModelDetailsPage.classificationTitle',
        defaultMessage: 'Class type performance'
    },
    extractionTitle: {
        id: 'customText.customTextModelDetailsPage.extractionTitle',
        defaultMessage: 'Entity type performance'
    },
    className: {
        id: 'customText.customTextModelDetailsPage.className',
        defaultMessage: 'Class name'
    },
    entityName: {
        id: 'customText.customTextModelDetailsPage.entityName',
        defaultMessage: 'Entity name'
    },
    emptyEntitiesMessage: {
        id: 'customText.customTextModelDetailsPage.emptyEntitiesMessage',
        defaultMessage: 'No entities available'
    },
    emptyIntentsMessage: {
        id: 'customText.customTextModelDetailsPage.emptyIntentsMessage',
        defaultMessage: 'No classes available'
    },
    warningExtraction: {
        id: 'customText.customTextModelDetailsPage.warningExtraction',
        defaultMessage: 'Entities that are neither labeled nor predicted in the test set will not be part of the displayed results.'
    },
    warningClassification: {
        id: 'customText.customTextModelDetailsPage.warningClassification',
        defaultMessage: 'Classes that are neither labeled nor predicted in the test set will not be part of the displayed results.'
    },
    dataSplitLearnMore: {
        id: 'customText.customTextModelDetailsPage.dataSplitLearnMore',
        defaultMessage: 'Learn more about data splitting.'
    },
    confusionMatrix: {
        id: 'customText.customTextModelDetailsPage.confusionMatrix',
        defaultMessage: 'Test set confusion matrix'
    },
    confusionMatrixTab: {
        id: 'customText.customTextModelDetailsPage.confusionMatrixTab',
        defaultMessage: 'Confusion matrix'
    },
    confusionMatrixTabDescription: {
        id: 'customText.customTextModelDetailsPage.confusionMatrixTabDescription',
        defaultMessage: `A confusion matrix is a specific table layout that visualizes the performance of an algorithm.
  All correct predictions are located in the diagonal of the table (highlighted in bold), so it is easy to visually
  inspect the table for prediction errors, as they will be represented by values outside the diagonal.`
    },
    learnMore: {
        id: 'customText.customTextModelDetailsPage.learnMore',
        defaultMessage: 'Learn more about confusion matrices.'
    },
    confusionMatrixEmptyStateMsg: {
        id: 'customText.customTextModelDetailsPage.confusionMatrixEmptyStateMsg',
        defaultMessage:
            "Your model's training is in progress/has failed. The confusion matrix will appear here once training is completed successfully."
    },
    confusionMatrixErrorStateMsg: {
        id: 'customText.modelDetailsPage.confusionMatrixErrorStateMsg',
        defaultMessage: 'Please make sure you include 10 or more examples (documents).'
    },
    confusionMatrixTabTextExtraction: {
        id: 'customText.customTextModelDetailsPage.confusionMatrixTabTextExtraction',
        defaultMessage:
            'Numbers on the diagonal are when the entity was correctly identified. Other numbers in the row show where it was incorrectly predicted as other entities'
    },
    confusionMatrixTabTextClassification: {
        id: 'customText.customTextModelDetailsPage.confusionMatrixTabTextClassification',
        defaultMessage:
            'Numbers on the diagonal are when the class was correctly identified. Other numbers in the row show where it was incorrectly predicted as other classes'
    },
    confusionMatrixNotAvailable: {
        id: 'customText.customTextModelDetailsPage.confusionMatrixNotAvailable',
        defaultMessage: 'Confusion matrix is not available for multi classification projects.'
    },
    datasetDistribution: {
        id: 'customText.customTextModelDetailsPage.datasetDistribution',
        defaultMessage: 'Dataset distribution'
    },
    testSetDetails: {
        id: 'customText.customTextModelDetailsPage.testSetDetails',
        defaultMessage: 'Test set details'
    }
});

const theme = getScopedTheme('ModelDetailsPage');

export const CustomTextModelDetailsPage = observer(() => {
    const history = useHistory();
    const location = useLocation();
    const { serviceName } = getPortalFromUrl();
    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();
    const { rootStore, dispatcher } = useGlobalState();
    const { modelName } = useParams<{ modelName: string }>();
    const modelEvaluationFetchingTracker = useProgressTracker();

    const [pivotTabsDisplay, setPivotTabsDisplay] = React.useState('block');

    const [filteredLabelInTestSetTab, setFilteredLabelInTestSetTab] = React.useState<string>(null);
    const [filteredPredictionInTestSetTab, setFilteredPredictionInTestSetTab] = React.useState<string>(null);
    const [modelPerformanceModalInfo, setModelPerformanceModalInfo] = React.useState(location.state as ModelPerformanceModalDto);
    const [selectedPivot, setSelectedPivot] = React.useState(modelPerformanceModalInfo?.tabKey ?? 'overview-tab');

    const currentProject = rootStore.currentCustomTextProjectStore;
    const modelGuidanceStore = currentProject.modelsGuidanceMap.get(modelName);
    const modelEvaluationStore = currentProject.modelsEvaluationMap.get(modelName);
    const trainedModel = currentProject.trainedModels.items.find(item => item.name === modelName);

    const isEvaluationDataDisplayable =
        trainedModel &&
        (modelEvaluationStore?.confusionMatrix?.length > 0 || modelEvaluationStore?.performanceMetricsBreakdown?.length > 0);

    const isPageLoading = !trainedModel || (trainedModel && !modelEvaluationStore && modelEvaluationFetchingTracker.state !== 'completed');

    const isExtractionProject = currentProject.projectKind === 'CustomEntityRecognition';
    const isConfusionMatrixDisabled = currentProject.projectKind === 'CustomMultiLabelClassification';

    const handlePivotChange = (tabKey: string) => {
        if (tabKey !== 'test-set-tab') {
            setModelPerformanceModalInfo(null);
            setFilteredLabelInTestSetTab(null);
            setFilteredPredictionInTestSetTab(null);
        }

        setSelectedPivot(tabKey);

        logPivotClick(serviceName, 'ModelPerfomance', modelGuidancePivotKeyToName(tabKey), currentProject.id);
    };

    React.useEffect(() => {
        window.onbeforeunload = () => {
            history.push(location.pathname, null);
        };

        return () => {
            window.onbeforeunload = null;
        };
    }, []);

    React.useEffect(() => () => history.push(history.location.pathname, null), []);

    React.useEffect(() => {
        if (isXLargeOrGreater) {
            setPivotTabsDisplay('block');
        } else {
            setPivotTabsDisplay('grid');
        }
    }, [isXLargeOrGreater]);

    React.useEffect(() => {
        if (trainedModel && (!modelEvaluationStore || !modelGuidanceStore)) {
            dispatcher.dispatch('getTrainedModelAllData', {
                trainedModelName: modelName,
                tracker: modelEvaluationFetchingTracker,
                toastData: {}
            });
        }
    }, [trainedModel, modelEvaluationStore, modelName]);

    return (
        <Page size="container-fluid" pageName="ModelDetailsPage" title={modelName} data-id="CustomTextModelDetails">
            {isPageLoading ? (
                <ShimmerFactory shimmerCount={10} />
            ) : (
                <div style={{ display: 'block' }}>
                    <EditableTitle title={modelName} />

                    <MessageBar messageBarType={MessageBarType.warning}>
                        <LuisText>
                            {isConfusionMatrixDisabled && `${localize(messages.confusionMatrixNotAvailable)} `}
                            {isExtractionProject ? localize(messages.warningExtraction) : localize(messages.warningClassification)}
                            &nbsp;
                            <ExternalLink
                                styles={{ root: { color: theme.warningLinkColor } }}
                                href={`https://aka.ms/${isExtractionProject ? 'ct-training-concepts' : 'ctc-training-concepts'}`}>
                                {localize(messages.dataSplitLearnMore)}
                            </ExternalLink>
                        </LuisText>
                    </MessageBar>

                    <Pivot
                        linkSize="large"
                        selectedKey={selectedPivot}
                        styles={{ root: { display: pivotTabsDisplay } }}
                        onLinkClick={link => handlePivotChange(link.props.itemKey)}>
                        <PivotItem itemKey={'overview-tab'} headerText={localize(messages.overview)}>
                            <OverviewTab
                                trainedModel={trainedModel}
                                capabilityName={currentProject.projectKind}
                                modelGuidanceStore={modelGuidanceStore}
                                currentProjectName={currentProject.name}
                                onLinkToOtherTabClick={handlePivotChange}
                                isExtractionProject={isExtractionProject}
                                modelEvaluationStore={modelEvaluationStore}
                            />
                        </PivotItem>

                        <PivotItem
                            itemKey={'evaluation-tab'}
                            headerText={localize(isExtractionProject ? messages.extractionTitle : messages.classificationTitle)}>
                            <ModelEvaluationTab
                                modelGuidanceStore={modelGuidanceStore}
                                isExtractionProject={isExtractionProject}
                                modelEvaluationStore={modelEvaluationStore}
                                isEvaluationDataDisplayable={isEvaluationDataDisplayable}
                            />
                        </PivotItem>

                        <PivotItem itemKey={'test-set-tab'} headerText={localize(messages.testSetDetails)}>
                            <CustomTextTestSetDetailsTab
                                {...modelPerformanceModalInfo}
                                selectedModelName={modelName}
                                filteredLabel={filteredLabelInTestSetTab}
                                filteredPrediction={filteredPredictionInTestSetTab}
                            />
                        </PivotItem>

                        <PivotItem itemKey={'data-distribution-tab'} headerText={localize(messages.datasetDistribution)}>
                            <DataDistributionTab
                                capabilityName={currentProject.projectKind}
                                isExtractionProject={isExtractionProject}
                                modelGuidanceStore={modelGuidanceStore}
                            />
                        </PivotItem>

                        {!isConfusionMatrixDisabled && (
                            <PivotItem itemKey={'confusion-matrix-tab'} headerText={localize(messages.confusionMatrixTab)}>
                                <ConfusionMatrixTab
                                    trainedModel={trainedModel}
                                    modelEvaluationStore={modelEvaluationStore}
                                    onCellClick={selectedLabels => {
                                        setFilteredLabelInTestSetTab(selectedLabels?.[1]);
                                        setFilteredPredictionInTestSetTab(selectedLabels?.[0]);
                                        handlePivotChange('test-set-tab');
                                    }}
                                    serviceName={isExtractionProject ? 'ner' : 'classification'}
                                    isEvaluationDataDisplayable={isEvaluationDataDisplayable}
                                    onOverviewTabClick={() => handlePivotChange('overview-tab')}
                                />
                            </PivotItem>
                        )}
                    </Pivot>
                </div>
            )}
        </Page>
    );
});
